<script setup>
import { computed, ref } from 'vue'
import { useRouter } from 'vue-router'
import UmisisModal from '../../../../../../../resources/js/components/umisis/generic/UmisisModal.vue'
import { useModalControl } from '../../../../../../../resources/js/composables/modal/useUmisisModal.js'
import InputText from '../../../../../../../resources/js/components/umisis/form/inputs/InputText.vue'
import { Field, FieldContent, FieldDescription, FieldLabel } from '../../../../../../../resources/js/components/ui/field/index.js'
import { Switch } from '@/components/ui/switch/index.js'
import { useAuthStore } from '@/stores/auth'
import { useChecklistTemplatesStore } from '../../stores/checklistTemplatesStore.js'

const props = defineProps({
  checklistTemplateable: {
    type: Object,
    default: null,
  },
  requiresChecklistTemplateableContext: {
    type: Boolean,
    default: true,
  },
  editRoute: {
    type: String,
    default: 'ChecklistTemplateEditPage',
  },
  redirectToEditPageAfterCreate: {
    type: Boolean,
    default: true,
  },
})

const emit = defineEmits(['created'])
const { componentRef: dialog, openModal, closeModal } = useModalControl()
const router = useRouter()
const authStore = useAuthStore()
const checklistTemplatesStore = useChecklistTemplatesStore('create-modal')

const title = ref('')
const description = ref('')
const isPublic = ref(false)
const loading = ref(false)
const errorMessage = ref(null)

const normalizedChecklistTemplateableId = computed(() => Number(props.checklistTemplateable?.id ?? 0))
const normalizedChecklistTemplateableType = computed(() => {
  return String(props.checklistTemplateable?.type ?? '').trim()
})
const hasChecklistTemplateableContext = computed(() => {
  return normalizedChecklistTemplateableId.value > 0 && normalizedChecklistTemplateableType.value.length > 0
})
const ownerUserId = computed(() => Number(authStore.user?.id ?? 0))
const canSubmit = computed(() => {
  return !loading.value
    && title.value.trim().length > 0
    && ownerUserId.value > 0
    && (!props.requiresChecklistTemplateableContext || hasChecklistTemplateableContext.value)
})

const fieldControlBaseClass = 'flex w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50'

const resetFields = () => {
  title.value = ''
  description.value = ''
  isPublic.value = false
  errorMessage.value = null
}

const handleOk = async (bvEvent) => {
  if (bvEvent) {
    bvEvent.preventDefault()
  }

  errorMessage.value = null

  if (!canSubmit.value) {
    errorMessage.value = props.requiresChecklistTemplateableContext
      ? 'A checklist template name and valid checklist-templateable context are required.'
      : 'A checklist template name is required.'
    return
  }

  loading.value = true

  try {
    const checklistTemplatePayload = {
      user_id: ownerUserId.value,
      title: title.value.trim(),
      description: description.value.trim().length > 0 ? description.value.trim() : null,
      public: isPublic.value,
      status: 'draft',
    }

    let checklistTemplate

    if (hasChecklistTemplateableContext.value) {
      checklistTemplate = await checklistTemplatesStore.itemCreate({
        ...checklistTemplatePayload,
        checklist_templateable_id: normalizedChecklistTemplateableId.value,
        checklist_templateable_type: normalizedChecklistTemplateableType.value,
      })
    } else {
      checklistTemplate = await checklistTemplatesStore.itemCreate(checklistTemplatePayload)
    }

    emit('created', checklistTemplate)
    closeModal()

    if (props.redirectToEditPageAfterCreate) {
      const routeParams = {
        checklistTemplateId: Number(checklistTemplate.id),
      }

      if (props.editRoute === 'BdsChecklistTemplateEditPage' && normalizedChecklistTemplateableId.value > 0) {
        routeParams.businessSystem = normalizedChecklistTemplateableId.value
      }

      await router.push({
        name: props.editRoute,
        params: routeParams,
      })
    }
  } catch (error) {
    errorMessage.value = error.response?.data?.message ?? error.message ?? 'Unable to create checklist template.'
  } finally {
    loading.value = false
  }
}

defineExpose({
  openModal,
  closeModal,
})
</script>

<template>
  <UmisisModal
    ref="dialog"
    title="Add Checklist Template"
    description="Provide a name and optional description for the new checklist template."
    ok-title="Create"
    cancel-title="Cancel"
    :ok-disabled="!canSubmit"
    @ok="handleOk"
    @show="resetFields"
  >
    <div class="flex flex-col gap-4">
      <InputText
        v-model="title"
        label="Name"
        placeholder="Checklist template name"
      />

      <Field>
        <FieldLabel for="checklist-template-create-description">
          Description
        </FieldLabel>
        <textarea
          id="checklist-template-create-description"
          v-model="description"
          rows="4"
          :class="fieldControlBaseClass"
          placeholder="Describe when this checklist template should be used..."
        />
      </Field>

      <Field orientation="horizontal" class="items-start rounded-md border p-3">
        <FieldContent>
          <FieldLabel for="checklist-template-create-public">
            Public template
          </FieldLabel>
          <FieldDescription>
            Other users can view and duplicate this template, but they cannot edit it.
          </FieldDescription>
        </FieldContent>
        <Switch id="checklist-template-create-public" v-model="isPublic" class="shrink-0" :disabled="loading" />
      </Field>

      <FieldDescription v-if="errorMessage" class="text-destructive font-medium">
        {{ errorMessage }}
      </FieldDescription>
    </div>
  </UmisisModal>
</template>
